// Client for the additional-account-emails API (0125):
// plain fetch helpers taking (httpBase, token), so the component stays free of
// transport details and the ws-client only has to forward them.
//
// Every mutating call returns the FULL refreshed list. The server already has to
// read it back to answer, and returning it means the caller never has to guess
// at the new state (who is default now, did the removal really land) or fire a
// second round-trip to find out.

export interface AccountEmail {
  id: string;
  email: string;
  /** The account default: what we show and notify unless a workspace overrides it. */
  isPrimary: boolean;
  verifiedAt: string;
  /**
   * This address IS `users.email` — the account's identity anchor. It cannot be
   * removed (the daemon derives the user id from it), which the UI has to be able
   * to explain rather than just rejecting the click.
   */
  isAnchor: boolean;
  /** False: listed on the account but never confirmed with a code, so it identifies nobody
   *  (no Slack match, no sign-in) until verified. Absent on an older relay, which reads as true. */
  proven?: boolean;
}

// The server's "no" (bad code, taken address, too many tries), as the wire code
// reportUiFailure reads to count it a refusal, not a fault.
const REFUSAL_CODE_BY_STATUS: Readonly<Record<number, string>> = {
  400: "invalid_request",
  401: "unauthenticated",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  429: "rate_limited",
};

class AccountEmailError extends Error {
  readonly code: string | undefined;
  constructor(message: string, status: number) {
    super(message);
    this.name = "AccountEmailError";
    this.code = REFUSAL_CODE_BY_STATUS[status];
  }
}

async function failure(resp: Response, fallback: string): Promise<AccountEmailError> {
  return new AccountEmailError(await readError(resp, fallback), resp.status);
}

async function readError(resp: Response, fallback: string): Promise<string> {
  try {
    const body = (await resp.json()) as { error?: string };
    return body.error ?? fallback;
  } catch (err) {
    // A non-JSON body (proxy error page, empty 502) is not actionable, but the
    // caller still needs SOMETHING to show — so fall back rather than throw.
    console.warn("[user-emails] non-JSON error body", err);
    return fallback;
  }
}

export async function listAccountEmails(httpBase: string, token: string): Promise<AccountEmail[]> {
  const resp = await fetch(`${httpBase}/api/auth/emails`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await failure(resp, "Couldn't load your emails");
  const body = (await resp.json()) as { emails: AccountEmail[] };
  return body.emails;
}

/**
 * Mail a verification code to a new address. `devCode` comes back only when the
 * relay has no mail provider configured (dev), so the flow stays testable
 * without a mailbox — it is never present in production.
 */
export async function startAddEmail(
  httpBase: string,
  token: string,
  email: string,
): Promise<{ devCode?: string }> {
  const resp = await fetch(`${httpBase}/api/auth/emails/start`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) throw await failure(resp, "Couldn't send the code");
  const body = (await resp.json()) as { devCode?: string };
  return { devCode: body.devCode };
}

export async function verifyAddEmail(
  httpBase: string,
  token: string,
  email: string,
  code: string,
): Promise<AccountEmail[]> {
  const resp = await fetch(`${httpBase}/api/auth/emails/verify`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (!resp.ok) throw await failure(resp, "That code didn't work");
  const body = (await resp.json()) as { emails: AccountEmail[] };
  return body.emails;
}

export async function removeAccountEmail(
  httpBase: string,
  token: string,
  id: string,
): Promise<AccountEmail[]> {
  const resp = await fetch(`${httpBase}/api/auth/emails/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await failure(resp, "Couldn't remove that address");
  const body = (await resp.json()) as { emails: AccountEmail[] };
  return body.emails;
}

export async function setPrimaryAccountEmail(
  httpBase: string,
  token: string,
  id: string,
): Promise<AccountEmail[]> {
  const resp = await fetch(`${httpBase}/api/auth/emails/${id}/primary`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw await failure(resp, "Couldn't change your default");
  const body = (await resp.json()) as { emails: AccountEmail[] };
  return body.emails;
}
